import Add from '@mui/icons-material/Add'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Delete from '@mui/icons-material/Delete'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { timezones_list } from 'src/consumer/constants'
import { checkAndGetTargetValue } from 'src/consumer/helpers/utilFunctions'
import {
  createDefaultClinicSchedule,
  getClinicScheduleTimezoneOffset,
  NEW_SLOT,
  WEEK_DAY_ORDER,
} from '../clinic-schedule-utils'
import './clinic-schedule-styles.css'
import {
  ClinicTimeSlotSelectionContainer,
  ScheduleInputContainer,
  ScheduleTabContainer,
  ScheduleTitleInputContainer,
} from './styledComponents'
// import ConfirmationModal, {
//   confirmationModalDefaultProps,
// } from 'src/components/controlCenter/ConfirmationModal'
import { components } from 'react-select'
import {
  CustomInput as CFormInput,
  CustomSelect,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import {
  ProfileTabButton,
  ProfileTabButtonContainer,
} from 'src/mentor/components/MentorProfilePage/MentorProfilePageStyles'
import { StyledAddStaffButton } from '../../onboardng/ClinicOnboardingStyles'
import { ReactSelectCustomContainer } from './styledComponents'

function SlotTimeSelectControl({ children, ...propData }) {
  return (
    <ReactSelectCustomContainer
      customInvalidColor={'#ff8181'}
      showBorder={true}
      invalid={propData?.selectProps?.isInvalid}
    >
      <components.SelectContainer {...propData}>{children}</components.SelectContainer>
    </ReactSelectCustomContainer>
  )
}

function cloneSlotList(slotList) {
  return Array.isArray(slotList) ? slotList.map((slot) => ({ ...slot })) : null
}

function cloneWeeklyHoursForDay(sourceDaySchedule, targetDaySchedule) {
  if (!sourceDaySchedule || !targetDaySchedule) {
    return targetDaySchedule
  }
  return {
    ...targetDaySchedule,
    ...sourceDaySchedule,
    title: targetDaySchedule?.title || sourceDaySchedule?.title,
    slotList: cloneSlotList(sourceDaySchedule?.slotList),
  }
}

function getPreviousDayOptions(weeklyHourMap, dayKey) {
  const dayIndex = WEEK_DAY_ORDER.indexOf(dayKey)
  if (dayIndex <= 0) {
    return []
  }
  return WEEK_DAY_ORDER.slice(0, dayIndex)
    .map((sourceDayKey) => {
      const sourceDaySchedule = weeklyHourMap?.[sourceDayKey]
      if (!sourceDaySchedule) {
        return null
      }
      return {
        key: sourceDayKey,
        label: sourceDaySchedule?.title,
        summary: getDayScheduleSummary(sourceDaySchedule),
      }
    })
    .filter(Boolean)
}

function formatSlotTime(time) {
  return dayjs(time, 'HH:mm:ss').format('h:mm A')
}

function getDayScheduleSummary(daySchedule) {
  if (!daySchedule?.available) {
    return 'Closed'
  }
  const slots = Array.isArray(daySchedule?.slotList) ? daySchedule.slotList : []
  if (slots.length === 0) {
    return 'No slots'
  }
  return slots
    .map((slot) => `${formatSlotTime(slot.startTime)} - ${formatSlotTime(slot.endTime)}`)
    .join(', ')
}

export default function ClinicScheduleComponent({
  teacherData,
  setTeacherData,
  invalidSchedules,
  disabled,
}) {
  const schedules = teacherData?.schedules || []
  const [selectedIndex, setSelectedIndex] = useState(0)

  const handleChangeSchedule = useCallback(
    (event, idx) => {
      if (typeof event === 'function') {
        setTeacherData((prev) => ({
          ...prev,
          schedules: (prev?.schedules || []).map((schedule, index) =>
            index === idx ? event(schedule) : schedule,
          ),
        }))
      } else {
        setTeacherData((prev) => ({
          ...prev,
          schedules: (prev?.schedules || []).map((schedule, index) =>
            index === idx
              ? { ...schedule, [event.target.id]: checkAndGetTargetValue(event || {}) }
              : schedule,
          ),
        }))
      }
    },
    [setTeacherData],
  )

  function handleChangeWeeklyHourMap(event, idx, day) {
    if (typeof event === 'function') {
      handleChangeSchedule(
        (prev) => ({
          ...prev,
          weeklyHourMap: {
            ...(prev.weeklyHourMap || {}),
            [day]: event(prev.weeklyHourMap?.[day] || {}),
          },
        }),
        idx,
      )
    } else {
      handleChangeSchedule(
        (prev) => ({
          ...prev,
          weeklyHourMap: {
            ...(prev.weeklyHourMap || {}),
            [day]: {
              ...(prev.weeklyHourMap?.[day] || {}),
              [event.target.id]: checkAndGetTargetValue(event),
            },
          },
        }),
        idx,
      )
    }
  }

  function changeDefault(e, idx) {
    setTeacherData((prev) => ({
      ...prev,
      schedules: (prev?.schedules || []).map((schedule, index) =>
        index === idx
          ? { ...schedule, defaultSchedule: e.target.checked }
          : { ...schedule, defaultSchedule: !e.target.checked },
      ),
    }))
  }

  function addSchedule() {
    setSelectedIndex(schedules?.length)
    setTeacherData((prev) => ({
      ...prev,
      schedules: [
        ...(prev?.schedules || []),
        {
          ...createDefaultClinicSchedule(
            getClinicScheduleTimezoneOffset(prev || teacherData),
            `Clinic Schedule ${(prev?.schedules || []).length + 1}`,
          ),
          defaultSchedule: (prev?.schedules || []).length === 0,
        },
      ],
    }))
  }
  function deleteSchedule(idx) {
    setSelectedIndex(Math.max(0, schedules?.length - 2))
    setTeacherData((prev) => {
      if (prev?.schedules?.[idx]?.defaultSchedule) {
        return {
          ...prev,
          schedules: (prev?.schedules || [])
            .filter((schedule, index) => index !== idx)
            .map((schedule, index) =>
              index === 0 ? { ...schedule, defaultSchedule: true } : schedule,
            ),
        }
      } else {
        return {
          ...prev,
          schedules: (prev?.schedules || []).filter((schedule, index) => index !== idx),
        }
      }
    })
  }

  useEffect(() => {
    setSelectedIndex((prev) => {
      if (!schedules?.length) {
        return 0
      }
      if (prev < 0 || prev >= schedules.length) {
        return schedules.length - 1
      }
      return prev
    })
  }, [schedules?.length])

  return (
    <>
      <ProfileTabButtonContainer>
        <ScheduleTabContainer>
          {schedules?.map((schedule, idx) => (
            <ProfileTabButton
              showBorder
              onClick={() => setSelectedIndex(idx)}
              selected={selectedIndex === idx}
              invalid={invalidSchedules?.[idx] === undefined ? undefined : !invalidSchedules?.[idx]}
              key={idx}
            >
              {schedule.title}{' '}
              {schedule.defaultSchedule && (
                <Chip sx={{ background: '#6a6a6a', color: 'white' }} label="Default" size="small" />
              )}
              {schedules?.length > 1 && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    deleteSchedule(idx)
                  }}
                >
                  <Delete />
                </IconButton>
              )}
            </ProfileTabButton>
          ))}
        </ScheduleTabContainer>
        {!disabled && (
          <IconButton onClick={addSchedule}>
            <Add />
          </IconButton>
        )}
      </ProfileTabButtonContainer>
      <Grid container spacing={2}>
        <Grid item sx={{ paddingLeft: '18px !important', width: '100%' }}>
          {schedules?.[selectedIndex] && (
            <ScheduleComponent
              schedule={schedules?.[selectedIndex]}
              changeDefault={changeDefault}
              disabled={disabled}
              handleChangeSchedule={handleChangeSchedule}
              handleChangeWeeklyHourMap={handleChangeWeeklyHourMap}
              idx={selectedIndex}
            />
          )}
        </Grid>
      </Grid>
    </>
  )
}

function ScheduleComponent({
  schedule,
  idx,
  handleChangeSchedule,
  changeDefault,
  handleChangeWeeklyHourMap,
  disabled,
}) {
  const [showDefaultDialog, setShowDefaultDialog] = useState(false)
  const weeklyHourMap = schedule?.weeklyHourMap || {}

  function handleChangeDefault() {
    // setConfirmationModalProps((prev) => ({
    //   ...prev,
    //   action: 'make schedule default',
    //   buttonColor: 'warning',
    //   body: `${schedule?.title} will become the default schedule for the clinic!`,
    //   onSubmitFunctions: [() => changeDefault(cachedEvent, idx)],
    //   visibility: true,
    //   visibilitySetter: setConfirmationModalProps,
    // }))
    setShowDefaultDialog(true)
  }
  function closeDefaultDialog() {
    setShowDefaultDialog(false)
  }

  function copyDayHours(targetDayKey, sourceDayKey) {
    handleChangeSchedule(
      (prevSchedule) => {
        const prevWeeklyHourMap = prevSchedule?.weeklyHourMap || {}
        const sourceDaySchedule = prevWeeklyHourMap?.[sourceDayKey]
        const targetDaySchedule = prevWeeklyHourMap?.[targetDayKey]
        if (!sourceDaySchedule || !targetDaySchedule) {
          return prevSchedule
        }
        return {
          ...prevSchedule,
          weeklyHourMap: {
            ...prevWeeklyHourMap,
            [targetDayKey]: cloneWeeklyHoursForDay(sourceDaySchedule, targetDaySchedule),
          },
        }
      },
      idx,
    )
  }

  return (
    <>
      <Accordion expanded={true}>
        <AccordionSummary
          focusVisibleClassName="mui-focus-remove-background"
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ cursor: 'unset !important' }}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <ScheduleInputContainer className="d-flex flex-wrap justify-content-stretch align-items-center gap-3 pb-3">
              <div className="d-flex justify-content-stretch align-items-end gap-2 w-100">
                <ScheduleTitleInputContainer>
                  <CFormInput
                    id="title"
                    value={schedule?.title}
                    label="Schedule Title"
                    disabled={disabled}
                    onChange={(e) => handleChangeSchedule(e, idx)}
                  />
                </ScheduleTitleInputContainer>
                {!schedule?.defaultSchedule && (
                  <StyledAddStaffButton
                    id="defaultSchedule"
                    checked={!!schedule?.defaultSchedule || false}
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                      handleChangeDefault({
                        target: { id: 'defaultSchedule', checked: true, type: 'checkbox' },
                      })
                    }}
                    disabled={disabled}
                  >
                    Make Default
                  </StyledAddStaffButton>
                )}
              </div>
              <div style={{ minWidth: '250px' }}>
                <CustomSelect
                  id="timeZone"
                  disabled={disabled}
                  value={schedule.timeZone ? decodeURIComponent(schedule.timeZone) : null}
                  options={timezones_list}
                  getOptionValue={(option) => option?.gmtvalue}
                  getOptionLabel={(option) => option?.name || option?.title}
                  onChangeEvent={(e) => {
                    handleChangeSchedule(
                      { ...e, target: { ...e.target, value: encodeURIComponent(e.target.value) } },
                      idx,
                    )
                  }}
                  selectType={selectTypes.string}
                  label="Time Zone"
                />
              </div>
            </ScheduleInputContainer>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <hr style={{ width: '100%' }}></hr>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {WEEK_DAY_ORDER.map((key) => [key, weeklyHourMap?.[key]])
                  .filter(([, daySchedule]) => !!daySchedule)
                  .map(([key, daySchedule]) => (
                    <Grid item xs={12} key={key}>
                      <WeekSchedule
                        schedule={daySchedule}
                        previousDayOptions={getPreviousDayOptions(weeklyHourMap, key)}
                        changeSchedule={(e) => {
                          handleChangeWeeklyHourMap(e, idx, key)
                        }}
                        onCopyFromDay={(sourceDayKey) => copyDayHours(key, sourceDayKey)}
                        disabled={disabled}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Dialog open={showDefaultDialog} onClose={closeDefaultDialog}>
        <DialogTitle>Make this schedule the default schedule?</DialogTitle>
        <DialogContent>
          This will make the schedule the default schedule for the clinic.
        </DialogContent>
        <DialogActions>
          <StyledAddStaffButton btnColor="none" onClick={closeDefaultDialog}>
            Cancel
          </StyledAddStaffButton>
          <StyledAddStaffButton
            onClick={() => {
              changeDefault(
                { target: { id: 'defaultSchedule', checked: true, type: 'checkbox' } },
                idx,
              )
              closeDefaultDialog()
            }}
          >
            Make Default
          </StyledAddStaffButton>
        </DialogActions>
      </Dialog>
      {/* <ConfirmationModal {...confirmationModalProps} newImplementation /> */}
    </>
  )
}

function WeekSchedule({ schedule, previousDayOptions = [], changeSchedule, onCopyFromDay, disabled }) {
  const slotList = schedule.slotList || []
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null)
  const copyMenuOpen = Boolean(copyMenuAnchor)

  function openCopyMenu(event) {
    setCopyMenuAnchor(event.currentTarget)
  }

  function closeCopyMenu() {
    setCopyMenuAnchor(null)
  }

  function copyFromDay(sourceDayKey) {
    onCopyFromDay?.(sourceDayKey)
    closeCopyMenu()
  }

  function changeAvailability(event) {
    if (slotList.length < 1 && event.target.checked) {
      addScheduleSlot()
    }
    changeSchedule(event)
  }

  function handleChangeSlot(event, idx) {
    if (typeof event === 'function') {
      changeSchedule((prev) => ({
        ...prev,
        slotList: (prev.slotList || []).map((slot, index) => (index === idx ? event(slot) : slot)),
      }))
    } else {
      changeSchedule((prev) => ({
        ...prev,
        slotList: (prev.slotList || []).map((slot, index) =>
          index === idx ? { ...slot, [event.target.id]: checkAndGetTargetValue(event) } : slot,
        ),
      }))
    }
  }

  function addScheduleSlot() {
    var slotToAdd = { ...NEW_SLOT }
    if (slotList?.length !== 0) {
      slotToAdd = {
        ...slotToAdd,
        startTime: dayjs(slotList?.[slotList.length - 1]?.endTime || NEW_SLOT.startTime, 'HH:mm:ss')
          ?.add(1, 'hour')
          .format('HH:mm:ss'),
        endTime: dayjs(slotList?.[slotList.length - 1]?.endTime || NEW_SLOT.endTime, 'HH:mm:ss')
          ?.add(2, 'hour')
          .format('HH:mm:ss'),
      }
    }

    changeSchedule((prev) => {
      return { ...prev, slotList: [...(prev.slotList || []), slotToAdd] }
    })
  }
  function deleteScheduleSlot(idx) {
    changeSchedule((prev) => ({
      ...prev,
      slotList: (prev.slotList || []).filter((slot, index) => index !== idx),
    }))
  }

  return (
    <div style={{ width: '100%' }}>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size={'small'}
                id="available"
                checked={!!schedule?.available || false}
                onChange={changeAvailability}
                disabled={disabled}
              />
            }
            label={schedule?.title}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            {!disabled && previousDayOptions.length > 0 ? (
              <>
                <Tooltip title={`Copy ${schedule?.title || 'day'} hours from an earlier day`}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={openCopyMenu}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Copy from
                  </Button>
                </Tooltip>
                <Menu anchorEl={copyMenuAnchor} open={copyMenuOpen} onClose={closeCopyMenu}>
                  {previousDayOptions.map((sourceDay) => (
                    <MenuItem key={sourceDay.key} onClick={() => copyFromDay(sourceDay.key)}>
                      <ListItemText primary={sourceDay.label} secondary={sourceDay.summary} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
            {!disabled && !!schedule?.available && (
              <IconButton onClick={addScheduleSlot}>
                <Add />
              </IconButton>
            )}
          </Stack>
        </Grid>
        {schedule?.available ? (
          <Grid item xs={12}>
            {slotList.map((slot, idx) => (
              <Grid container key={idx} sx={{ pl: 3 }}>
                <Grid item xs={12}>
                  <div className="d-flex justify-content-between align-items-center pt-1">
                    <div className="d-flex justify-content-start align-items-center gap-2">
                      <ClinicTimeSlotSelectionContainer>
                        <CustomSelect
                          id="startTime"
                          value={dayjs(slot.startTime, 'HH:mm:ss').format('HH:mm:ss')}
                          options={getSlotTimeOptions()}
                          getOptionValue={(option) => option.value}
                          getOptionLabel={(option) => option.label}
                          selectType={selectTypes.string}
                          onChangeEvent={(e) => handleChangeSlot(e, idx)}
                          isInvalid={
                            dayjs(slot.startTime, 'HH:mm:ss') >= dayjs(slot.endTime, 'HH:mm:ss') ||
                            (idx !== 0 &&
                              dayjs(slot.startTime, 'HH:mm:ss') <
                                dayjs(slotList?.[idx - 1].endTime, 'HH:mm:ss'))
                          }
                          makeSelectContainerTransparent={true}
                          components={{
                            Control: SlotTimeSelectControl,
                          }}
                          disabled={disabled}
                        />
                        {/* {dayjs(slot.startTime, 'HH:mm:ss').utcOffset(decodedTimeZone).format('HH:mm:A')} */}
                      </ClinicTimeSlotSelectionContainer>
                      <div>to</div>
                      <ClinicTimeSlotSelectionContainer>
                        <CustomSelect
                          id="endTime"
                          value={dayjs(slot.endTime, 'HH:mm:ss').format('HH:mm:ss')}
                          options={getSlotTimeOptions()}
                          getOptionValue={(option) => option.value}
                          getOptionLabel={(option) => option.label}
                          components={{
                            Control: SlotTimeSelectControl,
                          }}
                          isInvalid={
                            dayjs(slot.startTime, 'HH:mm:ss') >= dayjs(slot.endTime, 'HH:mm:ss') ||
                            (idx < slotList.length - 1 &&
                              dayjs(slotList?.[idx + 1]?.startTime, 'HH:mm:ss') <
                                dayjs(slot.endTime, 'HH:mm:ss'))
                          }
                          makeSelectContainerTransparent={true}
                          selectType={selectTypes.string}
                          onChangeEvent={(e) => handleChangeSlot(e, idx)}
                          disabled={disabled}
                        />
                      </ClinicTimeSlotSelectionContainer>
                    </div>
                    {(slotList?.length || 0) > 1 && !disabled && (
                      <div>
                        <IconButton onClick={() => deleteScheduleSlot(idx)}>
                          <Delete />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  {dayjs(slot.startTime, 'HH:mm:ss') >= dayjs(slot.endTime, 'HH:mm:ss') && (
                    <small className="text-danger">
                      Start time cannot be higher or equal to End time
                    </small>
                  )}
                  {idx !== 0 &&
                    dayjs(slot.startTime, 'HH:mm:ss') <
                      dayjs(slotList?.[idx - 1].endTime, 'HH:mm:ss') && (
                      <small className="text-danger">
                        This slot is overlapping with the previous slot
                      </small>
                    )}
                </Grid>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid item xs={12} sx={{ paddingLeft: '24px !important' }}>
            <strong>Closed</strong>
          </Grid>
        )}
      </Grid>
      <hr style={{ width: '100%' }}></hr>
    </div>
  )
}

const SLOT_TIME_OPTIONS = (() => {
  const options = []
  let startMoment = dayjs()?.startOf('day')
  const endMoment = dayjs()?.endOf('day')
  while (startMoment?.isBefore(endMoment)) {
    const timeValue = startMoment?.format('HH:mm:ss')
    const displayText = startMoment?.format('h:mmA')

    options?.push({ value: timeValue, label: displayText })
    startMoment = startMoment?.add(5, 'minutes')
  }
  return options
})()

const getSlotTimeOptions = () => {
  return SLOT_TIME_OPTIONS
}
